// src/components/HRComponents/PolicyForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { toast } from 'react-toastify';

import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import SelectDropdown from '../../customStyles/SelectDropdown';
import PolicyUploadDropzone from './PolicyUploadDropzone';

const PolicyForm = () => {
  const { portalName, id } = useParams(); // Extracts portalName and optional document ID from the URL
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [fileError, setFileError] = useState(null);

  const isEdit = !!id; // Boolean flag to determine if the form is in EDIT mode

  const [form, setForm] = useState({
    title: '',
    description: '',
    documentType: 'policy',
    scope: 'organization',
    category: '',
    requiresAcknowledgement: true,
    mandatoryForOnboarding: false,
    effectiveDate: '',
    expiryDate: '',
    renewalReminderDays: 30,
  });
  
  const [sourceType, setSourceType] = useState('uploaded'); // 'uploaded' | 'authored'
  const [file, setFile] = useState(null);
  const [contentHtml, setContentHtml] = useState('');

  // Fetch document categories for form select list
  useEffect(() => {
    api.get(SummaryApi.getPolicyCategories.url)
      .then((res) => { if (res.data.success) setCategories(res.data.data || []); })
      .catch(() => {});
  }, []);

  // Fetch existing document details if in EDIT mode [1]
  useEffect(() => {
    if (isEdit) {
      api.get(SummaryApi.getPolicyById.url(id))
        .then((res) => {
          if (res.data.success) {
            const doc = res.data.document;
            setForm({
              title: doc.title || '',
              description: doc.description || '',
              documentType: doc.documentType || 'policy',
              scope: doc.scope || 'organization',
              category: doc.category?._id || doc.category || '',
              requiresAcknowledgement: doc.requiresAcknowledgement ?? true,
              mandatoryForOnboarding: doc.mandatoryForOnboarding ?? false,
              effectiveDate: doc.effectiveDate ? doc.effectiveDate.split('T')[0] : '',
              expiryDate: doc.expiryDate ? doc.expiryDate.split('T')[0] : '',
              renewalReminderDays: doc.renewalReminderDays ?? 30,
            });
          }
        })
        .catch(() => {
          toast.error('Failed to load document details');
        });
    }
  }, [id, isEdit]);

  const updateField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  // Form submission logic handling both create and update operations
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error('Title is required');

    setIsSaving(true);
    try {
      if (isEdit) {
        // --- EDIT MODE: Put request with JSON payload ---
        const res = await api.put(SummaryApi.updatePolicy.url(id), form);
        if (res.data.success) {
          toast.success('Document updated successfully');
          navigate(`/${portalName}/hr/letter-policy/view/${id}`);
        }
      } else {
        // --- CREATE MODE: Post request with multipart/form-data payload ---
        if (sourceType === 'uploaded' && !file) return toast.error('Please attach a PDF or Word file');
        if (sourceType === 'authored' && !contentHtml.trim()) return toast.error('Please enter document content');

        const payload = new FormData();
        Object.entries(form).forEach(([key, value]) => {
          if (value !== '' && value !== null) payload.append(key, value);
        });
        
        // Append source and content fields
        payload.append('sourceType', sourceType);
        if (sourceType === 'uploaded' && file) {
          payload.append('file', file);
        } else if (sourceType === 'authored') {
          payload.append('contentHtml', contentHtml);
        }

        const res = await api.post(SummaryApi.createPolicyDocument.url, payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (res.data.success) {
          toast.success('Document created as draft');
          navigate(`/${portalName}/hr/letter-policy/view/${res.data.document._id}`);
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save document');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-4">
      {/* Dynamic Header based on active form state */}
      <div className="flex items-center gap-2 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="rounded-lg bg-red-50 border border-red-100 hover:border-primary text-textPrimary hover:text-primary p-1.5 sm:p-2 transition-colors"
        >
          <FaArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
        <h1 className="text-lg sm:text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit Letter / Policy' : 'New Letter / Policy'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border p-6 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="text-sm font-medium text-gray-700 mb-1 block">Title</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => updateField('title', e.target.value)}
              placeholder="e.g. Leave Policy 2026, Offer Letter - John Doe"
              className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none"
            />
          </div>

          <SelectDropdown
            label="Document Type"
            options={[{ label: 'Policy', value: 'policy' }, { label: 'Letter', value: 'letter' }]}
            value={form.documentType}
            onChange={(v) => updateField('documentType', v)}
            valueKey="value" labelKey="label"
          />

          <SelectDropdown
            label="Scope"
            options={[{ label: 'Organization-wide', value: 'organization' }, { label: 'Individual Employee', value: 'individual' }]}
            value={form.scope}
            onChange={(v) => updateField('scope', v)}
            valueKey="value" labelKey="label"
          />

          <SelectDropdown
            label="Category"
            options={[{ name: 'Uncategorized', _id: '' }, ...categories]}
            value={form.category}
            onChange={(v) => updateField('category', v)}
            valueKey="_id" labelKey="name" searchable
          />

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Effective Date</label>
            <input type="date" value={form.effectiveDate} onChange={(e) => updateField('effectiveDate', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none" />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Expiry Date (optional)</label>
            <input type="date" value={form.expiryDate} onChange={(e) => updateField('expiryDate', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none" />
          </div>

          <div className="sm:col-span-2">
            <label className="text-sm font-medium text-gray-700 mb-1 block">Description</label>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none"
            />
          </div>
        </div>

        <div className="flex items-center gap-6 border-t pt-4">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.requiresAcknowledgement} onChange={(e) => updateField('requiresAcknowledgement', e.target.checked)} />
            Requires employee acknowledgement
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.mandatoryForOnboarding} onChange={(e) => updateField('mandatoryForOnboarding', e.target.checked)} />
            Mandatory during onboarding
          </label>
        </div>

        {/* Hides file upload / text authoring tools when editing existing draft metadata */}
        {!isEdit && (
          <div className="border-t pt-4">
            <div className="flex gap-4 mb-3">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="radio" checked={sourceType === 'uploaded'} onChange={() => setSourceType('uploaded')} />
                Upload File (PDF / Word)
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="radio" checked={sourceType === 'authored'} onChange={() => setSourceType('authored')} />
                Write In-App
              </label>
            </div>

            {sourceType === 'uploaded' ? (
              <PolicyUploadDropzone file={file} onFileSelect={(f, err) => { setFile(f); setFileError(err); }} error={fileError} />
            ) : (
              <textarea
                rows={8}
                placeholder="Type or paste the document content..."
                value={contentHtml}
                onChange={(e) => setContentHtml(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none"
              />
            )}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={() => navigate(-1)} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm">
            Cancel
          </button>
          <button type="submit" disabled={isSaving} className="px-4 py-2 rounded-lg bg-primary text-white text-sm disabled:opacity-50">
            {isSaving ? 'Saving...' : isEdit ? 'Save Changes' : 'Save as Draft'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PolicyForm;